import { useEffect, useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const input =
  "w-full border border-white/12 bg-black px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-ochre";
const label = "mono block text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400";

const EMPTY = { verdict: "", standing: "", ground_name: "", detail: "", name: "", email: "", phone: "",
  may_credit: false };

/** A local official confirming, or correcting, the ground we hold for one county. */
export const ConfirmGround = ({ county, venue, onClose }) => {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/league-readiness/confirm").then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const ready = form.verdict && form.standing && form.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSending(true);
      const { data } = await api.post("/league-readiness/confirm", { ...form, county });
      setDone(data);
      toast.success(`Recorded for ${county}. Thank you.`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm md:p-10"
      data-testid="confirm-ground-modal">
      <div className="w-full max-w-2xl border border-white/12 bg-[#0b0b0b] p-6 md:p-9">
        <div className="flex items-start gap-6">
          <div>
            <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-ochre">
              {county} · {venue || "No ground on file"}
            </p>
            <h2 className="display-3 mt-3">{done ? "On The Record" : "Confirm The Ground"}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="confirm-ground-close"
            className="ml-auto shrink-0 border border-white/12 p-2 text-zinc-400 transition-colors hover:border-maasai hover:text-white">
            <X size={14} />
          </button>
        </div>

        {done ? (
          <div className="mt-7" data-testid="confirm-ground-done">
            <p className="body-copy">{done.message}</p>
            <p className="mono mt-6 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-500">
              {done.reports_for_county} report{done.reports_for_county === 1 ? "" : "s"} now on file for {county}
            </p>
            <button type="button" onClick={onClose} className="btn-primary mt-8" data-testid="confirm-ground-finish">
              Back to the register
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-6" data-testid="confirm-ground-form">
            <div>
              <span className={label}>What is true about this ground?</span>
              <div className="mt-3 space-y-2">
                {(meta?.verdicts || []).map((v) => (
                  <label key={v.id}
                    className={`flex cursor-pointer items-start gap-3 border p-3.5 text-xs leading-relaxed transition-colors ${
                      form.verdict === v.id ? "border-ochre bg-ochre/5 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"
                    }`}>
                    <input type="radio" name="verdict" value={v.id} checked={form.verdict === v.id}
                      onChange={set("verdict")} className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#E5A91E]"
                      data-testid={`confirm-verdict-${v.id}`} />
                    <span>{v.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="standing">How do you know?</label>
                <select id="standing" className={`${input} mt-3`} value={form.standing} onChange={set("standing")}
                  data-testid="confirm-standing" required>
                  <option value="">Choose one</option>
                  {(meta?.standing || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="ground_name">
                  The ground&rsquo;s name <span className="text-zinc-600">(if ours is wrong)</span>
                </label>
                <input id="ground_name" className={`${input} mt-3`} value={form.ground_name}
                  onChange={set("ground_name")} data-testid="confirm-ground-name" />
              </div>
            </div>

            <div>
              <label className={label} htmlFor="detail">Anything we should know about it</label>
              <textarea id="detail" rows={3} className={`${input} mt-3 resize-none`} value={form.detail}
                onChange={set("detail")} data-testid="confirm-detail"
                placeholder="The state of the pitch, whether it has lights, who uses it and how often." />
            </div>

            <div className="grid gap-5 sm:grid-cols-3">
              <div>
                <label className={label} htmlFor="name">Your name</label>
                <input id="name" className={`${input} mt-3`} value={form.name} onChange={set("name")}
                  data-testid="confirm-name" required />
              </div>
              <div>
                <label className={label} htmlFor="email">Your email</label>
                <input id="email" type="email" className={`${input} mt-3`} value={form.email}
                  onChange={set("email")} data-testid="confirm-email" required />
              </div>
              <div>
                <label className={label} htmlFor="phone">Phone <span className="text-zinc-600">(optional)</span></label>
                <input id="phone" className={`${input} mt-3`} value={form.phone} onChange={set("phone")}
                  data-testid="confirm-phone" />
              </div>
            </div>

            <label className="flex cursor-pointer gap-3 text-xs leading-relaxed text-zinc-300">
              <input type="checkbox" checked={form.may_credit} onChange={set("may_credit")}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#E5A91E]" data-testid="confirm-may-credit" />
              <span>You may credit me by name in the register. Leave it unticked and we keep you out of it.</span>
            </label>

            <div className="grid gap-6 border-t border-zinc-900 pt-6 md:grid-cols-2">
              <div data-testid="confirm-what-it-does">
                <p className={label}>What this does</p>
                <ul className="mt-3 space-y-2">
                  {(meta?.what_it_does || []).map((t) => (
                    <li key={t} className="flex gap-2.5 text-[0.7rem] leading-relaxed text-zinc-400">
                      <Check size={11} className="mt-1 shrink-0 text-ochre" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div data-testid="confirm-not-this">
                <p className={label}>What it is not</p>
                <ul className="mt-3 space-y-2">
                  {(meta?.not_this || []).map((t) => (
                    <li key={t} className="flex gap-2.5 text-[0.7rem] leading-relaxed text-zinc-400">
                      <X size={11} className="mt-1 shrink-0 text-maasai" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <button type="submit" disabled={!ready || sending}
              className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="confirm-submit">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Put this on the record
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
